import type { Money } from './types';

const integerFormat = new Intl.NumberFormat('en-US');
const shortDateFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

export function formatCount(value: number | null | undefined): string {
  return value == null ? '—' : integerFormat.format(Math.round(value));
}

export function formatDecimal(value: number | null | undefined, digits = 2): string {
  return value == null ? '—' : value.toFixed(digits);
}

export function formatPercent(value: number | null | undefined, digits = 1): string {
  return value == null ? '—' : `${(value * 100).toFixed(digits)}%`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${String(total % 60).padStart(2, '0')}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/** Primary currency first; other currencies are listed after it. */
export function formatMoneyList(values: Money[], primary = 'USD'): string {
  if (values.length === 0) return '—';
  return [...values]
    .sort((a, b) => Number(b.currency === primary) - Number(a.currency === primary) || a.currency.localeCompare(b.currency))
    .map((value) => formatMoney(value.amount, value.currency))
    .join(' · ');
}

export function amountIn(values: Money[], currency: string): number {
  return values.find((value) => value.currency === currency)?.amount ?? 0;
}

export function formatShortDate(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? isoDate : shortDateFormat.format(parsed);
}

const shortDateWithYearFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const clockFormat = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });

/** "Sep 1 – Sep 30"; years are shown when the range is not entirely in the current year. */
export function formatDateRange(start: string, end: string): string {
  const showYear = start.slice(0, 4) !== end.slice(0, 4) || end.slice(0, 4) !== String(new Date().getFullYear());
  const format = (isoDate: string) => {
    const parsed = new Date(`${isoDate}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) return isoDate;
    return showYear ? shortDateWithYearFormat.format(parsed) : shortDateFormat.format(parsed);
  };
  return start === end ? format(start) : `${format(start)} – ${format(end)}`;
}

/** Local wall-clock time with the zone abbreviation, e.g. "10:12 AM EDT". */
export function formatClockTime(isoDateTime: string): string {
  const parsed = new Date(isoDateTime);
  return Number.isNaN(parsed.getTime()) ? isoDateTime : clockFormat.format(parsed);
}

/** Today's date in the viewer's timezone as YYYY-MM-DD (the value format of date inputs). */
export function localIsoDate(date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Inclusive number of days between two YYYY-MM-DD dates. */
export function daySpan(start: string, end: string): number {
  return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) + 1;
}

export function humanizeLabel(label: string): string {
  const spaced = label.replace(/[_-]+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : label;
}
